// @ts-check
import { gql } from '@apollo/client/core';
import { Image } from '@react-pdf/renderer';
import React from 'react';
import { Box, Flex, QRCode, Text } from '../../pdf-design-system';
import AveryPCL8051, { LabelItem } from '../AveryTemplates/AveryPCL8051';

export default function QRCodeLabels({ data, locale, ...rest }) {
  const { collection, collectionId } = data;
  const sections = afterQuery(data, rest);
  const products = sections
    .reduce((acc, section) => {
      const products = section?.registration?.registeredProducts?.nodes;
      acc = acc.concat(products);
      return acc;
    }, [])
    .map((product) => {
      return {
        ...product,
        sortNumber: sortNumber[product.productId],
      };
    })
    .sort((a, b) => (a.sortNumber > b.sortNumber ? 1 : -1));

  return (
    <AveryPCL8051>
      {products.map((product) => {
        const [, , ava, subAva] = product.product.regionHierarchy;
        return (
          <LabelItem>
            <Flex
              style={{
                width: '100%',
                padding: '2mm',
                alignItems: 'flex-start',
                paddingTop: '5mm',
              }}
            >
              <Flex
                style={{
                  width: '100%',
                  flexDirection: 'column',
                  flexGrow: 1,
                  height: '100%',
                  paddingRight: 10,
                }}
              >
                <Text style={{ color: '#888' }}>{product.producer.name}</Text>
                <Text style={{ fontSize: 12, marginVertical: 3 }}>
                  {product.product.name}
                </Text>
                <Text style={{ color: '#888' }}>
                  {[ava, subAva].filter(Boolean).join(', ')}
                </Text>
                <Box style={{ flexGrow: 1 }} />
                <Image
                  src={collection?.logo?.fixed.src}
                  style={{ height: 40, width: 60 }}
                />
              </Flex>
              <Flex
                style={{
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                {/* Only for UK press tasting at the moment */}
                {collectionId === '61bb727c5f943b143bfe005b' &&
                product.sortNumber ? (
                  <Box
                    style={{
                      paddingVertical: 2,
                      paddingHorizontal: 5,
                      backgroundColor: '#efefef',
                      borderRadius: 999,
                      border: 1,
                      borderColor: '#ddd',
                      textAlign: 'center',
                    }}
                    {...rest}
                  >
                    <Text>Wine #{product.sortNumber}</Text>
                  </Box>
                ) : (
                  <Box style={{ flexGrow: 1 }} />
                )}
                <QRCode
                  margin={0}
                  url={`${collection.site.publicUrl}/${product.productId}`}
                  style={{
                    height: '2cm',
                    width: '2cm',
                    marginHorizontal: '4mm',
                    marginBottom: '1mm',
                  }}
                />
              </Flex>
            </Flex>
          </LabelItem>
        );
      })}
    </AveryPCL8051>
  );
}

QRCodeLabels.fragment = gql`
  fragment California_QRCodeLabels on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      logo {
        fixed(height: 120, width: 180) {
          src
        }
      }
      site {
        publicUrl
      }
    }
    sections {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
          name
          registeredProducts {
            nodes {
              producer {
                name
              }
              productId
              product {
                productId
                name
                regionHierarchy
              }
            }
          }
        }
      }
    }
  }
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}

// These are hard-coded for the UK Press Tasting
// Not sure if there is a better way to get them loaded
// Currently we can't import custom data to a single event
const sortNumber = {
  '5b7a608980d162276a812308': 1,
  '61f94b7db841830ee63ad988': 2,
  '5c41bfe6a9a4871f715b26e0': 3,
  '61fd724121837a0ef36678a9': 4,
  '61fd2336ba77f60ef9cff8f3': 5,
  '61f31f56ec68d10ef094f681': 6,
  '61eed7c5200b570ef1388905': 7,
  '618179d1ca808175d7ec3169': 8,
  '6196fa5d5871390f20b6101f': 9,
  '6183b5642770420f13530b25': 10,
  '61827e6098ff1f0f24fcfa5a': 11,
  '61f925c1b841830ee63ad91b': 12,
  '61eed76e3c12170ef865b3a5': 13,
  '60f0d4cf1bd3981a6c21e6f4': 14,
  '618286a798ff1f0f24fcfa66': 15,
  '61a675514e6ace59f4fefc82': 16,
  '601c2167352c9a1a0b9541a3': 17,
  '60e762005d31e31a38f0c4fa': 18,
  '6200f3b91c53213e8278da43': 19,
  '5fb7b1051a13c519e69283ba': 20,
  '6183b7492770420f13530b34': 21,
  '6234aec73b58850f12da867b': 22,
  '61fcdf5a5c8c5f0f377a9852': 23,
  '5e33733974c4020e9489c2e3': 24,
  '60b5f2a90c48dc1a179318d2': 25,
  '61a8012c68d9fd6ed78ca424': 26,
  '618e4fd5411a8031b5d07da2': 27,
  '61f9160e3fa3970684465766': 28,
  '61f9618f3623b46d8d32f5f7': 29,
  '61f96fbf0574a714a37d5cfe': 30,
  '5f727ba49c683f1a6fc3723c': 31,
  '61fbb274d4ff2f0f09b7d633': 32,
  '605231a7d2c2da1a49f94db6': 33,
  '61fcecfd5c8c5f0f377a9863': 34,
  '61fd240121837a0ef36677cc': 35,
  '60cccf232344821a9a44efe9': 36,
  '62026c4eb142be42ae918b7c': 37,
  '61f462542e8078403c0fc97f': 38,
  '5dcdaac9e68a0e15d4e512de': 39,
  '61fd3a1721837a0ef3667811': 40,
  '5de6dd779255ee0e13e13192': 41,
  '61e761cc381db00ee4445aa7': 42,
  '6197d322937a220f102e74f0': 43,
  '61f16d2adbb8f30ed5b9b2cb': 44,
  '61fd666221837a0ef36678a4': 45,
  '60eddf96cc10011a3d8e0ea2': 46,
  '61fd30b7ba77f60ef9cff938': 47,
  '620cc414a3424c0efe2ca68a': 48,
  '6203ea323d091809c08e537e': 49,
  '61ee812b67f4980321cbd03c': 50,
  '61e553c0479dab0ef75ea998': 51,
  '618c6af344c6274283bebdbf': 52,
  '61fd24abba77f60ef9cff8fb': 53,
  '621cbc6cf242e30f46ea5b56': 54,
  '61fd61db21837a0ef366789e': 55,
  '620cc294835d5f0f04d3090b': 56,
  '61fa8f6cbc5dc20ef658f79d': 57,
  '5fbb87a4a33bf51a12e2e050': 58,
  '621cde785c49030f127103ca': 59,
  '61fa8fecbc5dc20ef658f7a1': 60,
  '61eae961a3c1b70f1fabe76e': 61,
  '61fd5676ba77f60ef9cff991': 62,
  '61fd5101ba77f60ef9cff98b': 63,
  '620104371c53213e8278da6c': 64,
  '5fc1069f71371222761219f9': 65,
  '621ce3fb5c49030f127103ef': 66,
  '6192f4a56afa641e3299ef4f': 67,
  '617a833bba255428dd4150cb': 68,
  '6230a84d4808590f1040ce43': 69,
  '6200f843f731a2154a59a254': 70,
  '61fc2992d4ff2f0f09b7d7d0': 71,
  '61e7658ace13670eeb365f33': 72,
  '6230a8b723ceaa0f09239aab': 73,
  '61eeb74c200b570ef13888ae': 74,
  '61e8365e9ece7812a389aed0': 75,
  '61e762afce13670eeb365f2c': 76,
  '62001f4bba77f60ef9cffac2': 77,
  '61ea8553a3c1b70f1fabe70d': 78,
  '6196fb8d831d3e0f270311a1': 79,
  '61f94843b841830ee63ad95d': 80,
  '61fc2a705b8de73874c6bd73': 81,
  '60edda51cc10011a3d8e0e91': 82,
  '621e21e91dd8d65269e12eeb': 83,
  '61e763e5381db00ee4445aac': 84,
  '61e761ffce13670eeb365f2b': 85,
  '61a675a4dbb7c20f31d20a1d': 86,
  '6093c4b78d0ceb1a1a0d1314': 87,
  '61eac041b6eca40f18bec505': 88,
  '5fb7813885b8227f1e16e037': 89,
  '61fd5fa621837a0ef366789d': 90,
  '61e95774ead8e50f4aebf2da': 91,
  '5fc1048271371222761219e4': 92,
  '620cc138a3424c0efe2ca663': 93,
  '61eacc01a3c1b70f1fabe762': 94,
  '61f0085e6f8ccd0ee2a69274': 95,
  '6216587e7e5ef80f03a85fbd': 96,
  '61f94a34b841830ee63ad983': 97,
  '62003000f731a2154a59a0e0': 98,
  '5efc871c44f8041a0309b0d5': 99,
  '61704d750f18330f1edfafe6': 100,
  '60f18877c1f24d1a1b262d18': 101,
  '619bdd7915aa2a0f1e9a4868': 102,
  '61fd78faba77f60ef9cff9d0': 103,
  '6196fb615871390f20b61020': 104,
  '61827f1b98ff1f0f24fcfa5b': 105,
  '61a807a268d9fd6ed78ca44d': 106,
  '60b6882ab1ddcc1a45c4fbbd': 107,
  '61eeb940200b570ef13888b1': 108,
  '5e3370d674c4020e9489c2c8': 109,
  '6234afa03b58850f12da867c': 110,
  '6196f1415871390f20b61000': 111,
  '615cc83e5f5ba30f2988d205': 112,
  '61a8018568d9fd6ed78ca428': 113,
  '61780bb14df98e0e05f05561': 114,
  '61fab63fd4ff2f0f09b7d3ea': 115,
  '5fb7afee84115c19e07b512e': 116,
  '6214b413e502b80f01fb9850': 117,
  '61ba975713eae00f54b13b7f': 118,
  '6234b0a06c76ec0f186f6f36': 119,
  '61fbb2b1d4ff2f0f09b7d639': 120,
  '6234b2693b58850f12da867f': 121,
  '6234b1ea3b58850f12da867d': 122,
  '5dcc8f15e74f1f0dc8d74049': 123,
  '6234b2fe6c76ec0f186f6f3d': 124,
  '5fbb98bf52ad4e1a1871c3d8': 125,
  '60ba6c19a05c971a4e548802': 126,
  '61fd584d21837a0ef3667885': 127,
  '61e804bb381db00ee4445b4b': 128,
  '61f95d677bc4520f0ed90fdd': 129,
  '61fc2aafd4ff2f0f09b7d7d2': 130,
  '61fbc164d4ff2f0f09b7d64e': 131,
  '5fb698ad71c7d219e75483bb': 132,
  '61f16a4020a481594a8a63d3': 133,
  '5fd11e6ff586543b4b167721': 134,
  '61ea9bdbb6eca40f18bec4f5': 135,
  '621ce4eef925ce0f18edb3c3': 136,
  '5fbb924a52ad4e1a1871c3c9': 137,
  '61fd3e53ba77f60ef9cff950': 138,
  '61fd5a3fba77f60ef9cff99e': 139,
  '61fd5b18ba77f60ef9cff9a2': 140,
  '61fa92ccbc5dc20ef658f7b2': 141,
  '6234b3fa6c76ec0f186f6f3e': 142,
  '621cc9ecf925ce0f18edb358': 143,
  '62010803f731a2154a59a28a': 144,
  '5fc1068371371222761219f5': 145,
  '5fc1061371371222761219f0': 146,
  '61fd6418ba77f60ef9cff9b5': 147,
  '6234b43c3b58850f12da8683': 148,
  '61f30e58f5c8c401f247f900': 149,
  '6234b46e6c76ec0f186f6f3f': 150,
  '61eebb42200b570ef13888b4': 151,
  '61f327a5f5c8c401f247f92b': 152,
  '619bd9298eb3b60f17501f0a': 153,
  '60efe673db21fa1a7b8fd644': 154,
  '61eed80c3c12170ef865b3a7': 155,
  '6196f9f55871390f20b6101e': 156,
  '5dcd54d9ffa03c0ed346d637': 157,
  '61f012a96f8ccd0ee2a6927b': 158,
  '6183b3772770420f13530b21': 159,
  '61e836879ece7812a389aed1': 160,
  '5ef0e8451479d12520b5ed7f': 161,
  '60c35bb15685011a0c1ad0ac': 162,
  '61927517b6a2cc5492a7b172': 163,
  '5fbb7e2f9126bc30ce16f1f2': 164,
  '62026c60b6c9a942b5db76f4': 165,
  '5fbc037052ad4e1a1871c493': 166,
  '61fc28b95b8de73874c6bd71': 167,
  '60e78cd92715831a322eb08a': 168,
  '619bfbd88eb3b60f17501f92': 169,
  '61f022bac6028a0edb9a6978': 170,
  '61786a4dbed6630e031c0e70': 171,
  '61a69ed43097af0f26e4866e': 172,
  '61aa4cf500df091c4a521d57': 173,
  '5bd987394237f82ef4db8085': 174,
  '60a7baca5d29511a055ab01a': 175,
  '61fcee76f1de4d0f312881c4': 176,
  '61fd3c9a21837a0ef366781a': 177,
  '61fd51f2ba77f60ef9cff98e': 178,
  '6203e7383d091809c08e537c': 179,
  '621cdf26f925ce0f18edb394': 180,
  '60ebec2217a8161a4e6b60bf': 181,
  '5bf29f82dfa9441adce1e6dd': 182,
  '61fd53f7ba77f60ef9cff990': 183,
  '61fab911ff2e640f10397061': 184,
  '5efb16af1e13f67d81de927e': 185,
  '621cca645c49030f12710381': 186,
  '61fd5b9e21837a0ef3667894': 187,
  '5dcbd3c338d6a522f3780445': 188,
  '6230a6be23ceaa0f09239a8c': 189,
  '608937587154d51a1e9309f5': 190,
  '5fc73df6708c8a1a41c75423': 191,
  '61137e68d3e8551842681351': 192,
  '6203ddde3d091809c08e535d': 193,
  '6062ee815494bf1a7a612ea7': 194,
  '6203e2791eb9d509c6e060c5': 195,
  '6089d4f0692d461a6226ac4d': 196,
  '5bec7c1e68cd405b18bcc63a': 197,
  '60eb216e17a8161a4e6b606e': 198,
  '6203d9f8440e95017f302c66': 199,
  '60f935251d29781a437bd644': 200,
  '61854d92199cfa0f58a8b8f1': 201,
  '5b868d956a830d5150c42984': 202,
  '621ccb4ef925ce0f18edb35d': 203,
  '5bec7e42b461595b17ae5a24': 204,
  '61fcf18cf1de4d0f312881c9': 205,
  '61eaeb83a3c1b70f1fabe76f': 206,
  '61f7cfca2e8078403c0fca63': 207,
  '5b866a1dab4f912228b372ad': 208,
  '61f92f23b841830ee63ad929': 209,
  '620fe3a04badbc0f2478f2a7': 210,
  '60f0d20d1bd3981a6c21e6f3': 211,
  '622b5dc62f956e0f2479c608': 212,
  '61f80ead2e8078403c0fcb23': 213,
  '6182804e1552ad0f2a077ce0': 214,
  '5de5f0c26113060e471ec91c': 215,
  '60c3493f1bb1a91a126a5518': 216,
  '61f948e1b841830ee63ad96c': 217,
  '60f166fefdd0fa1a158014c8': 218,
  '61fd4d0021837a0ef3667861': 219,
  '5e3320e574c4020e9489bfae': 220,
  '61a79c46f8c8610f327e5861': 221,
  '61a51f16f8d5a20f2cd39fa0': 222,
  '6086f4659dc12e19fd7ebe40': 223,
  '5fc3ae2332648f19e3783332': 224,
  '61e80298ce13670eeb365fba': 225,
  '6234b5863b58850f12da8686': 226,
  '60b68be26859921a4b480e90': 227,
  '61f9465ece2b8c0eeca4fce1': 228,
  '61fd503c21837a0ef3667867': 229,
  '6061e7b40eea591a80a41c19': 230,
  '61f96e71c37338149d7bebf8': 231,
  '61f148eddeb9570edbafff16': 232,
  '5bd9881d68fcd42eef97871a': 233,
  '601da11e46732f3401292fdb': 234,
  '6061e9d10eea591a80a41c1f': 235,
  '6200fdb6f731a2154a59a262': 236,
  '6200f4331c53213e8278da4a': 237,
  '620102b7f731a2154a59a273': 238,
  '6183b5ba2770420f13530b28': 239,
  '6200fff01c53213e8278da65': 240,
  '61f16e67dbb8f30ed5b9b2cd': 241,
  '61fac36dff2e640f10397071': 242,
  '5d5af59f90782716ce17b7c9': 243,
  '61fd3b3021837a0ef3667818': 244,
  '61ee815667f4980321cbd03f': 245,
  '61e957bbead8e50f4aebf2db': 246,
  '61fd2d66ba77f60ef9cff922': 247,
  '621d02575c49030f12710433': 248,
  '5f3c13d22c775b1a819ba624': 249,
  '61fcec31f1de4d0f312881c1': 250,
  '60c33bb61bb1a91a126a54ba': 251,
  '6200f18f1c53213e8278da3e': 252,
  '621ce35f5c49030f127103ea': 253,
  '620020ffba77f60ef9cffac4': 254,
  '5fc5211532648f19e3783507': 255,
  '621ce4615c49030f127103f0': 256,
  '61fd5b9d21837a0ef3667893': 257,
  '621cd5b9f925ce0f18edb365': 258,
  '61f30b80ec68d10ef094f64f': 259,
  '60ba51eca05c971a4e5487f3': 260,
  '61f94418ce2b8c0eeca4fcdd': 261,
  '61fbb2edff2e640f103972e0': 262,
  '60a7ab496012552d428e5280': 263,
  '620cc1b7835d5f0f04d30906': 264,
  '61fc2caa5b8de73874c6bd78': 265,
  '5fbe2e954c70c41a3e369f0d': 266,
  '61f01152c6028a0edb9a6960': 267,
  '5f1f343ab4deca3a1c96faf1': 268,
  '5fb792675862ab7f18fbfdce': 269,
  '5fc6635c2662231a478bd968': 270,
  '62001d2aba77f60ef9cffabe': 271,
  '620021b1f731a2154a59a0d4': 272,
  '61fa9c0815ea680ef0f95dc8': 273,
  '6230a89f4808590f1040ce4a': 274,
  '61fc2f20d4ff2f0f09b7d7e2': 275,
  '60f69e03b9a88f1a2fa920f9': 276,
  '61fd6565ba77f60ef9cff9b7': 277,
  '61e765f2ce13670eeb365f35': 278,
  '6045f24d4d29821a300a82d2': 279,
  '61fd6324ba77f60ef9cff9b3': 280,
  '5dd7b78a09e4d26bc83c7154': 281,
  '5de91b828ee1590e25affeaa': 282,
  '61eac4a3a3c1b70f1fabe752': 283,
  '61eaec58a3c1b70f1fabe770': 284,
  '5d6022481997a734bf1a0d78': 285,
  '61fd68c4ba77f60ef9cff9bb': 286,
  '62002e0cf731a2154a59a0dc': 287,
};
